
import { useEffect, useRef, useState } from 'react';

type AnimationType = 'fadeUp' | 'zoomIn' | 'slideLeft' | 'slideRight' | 'scaleUp';

interface ScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  delay?: number;
  animation?: AnimationType;
}

const animationClassMap: Record<AnimationType, { hidden: string; visible: string }> = {
  fadeUp: { hidden: 'scroll-hidden', visible: 'scroll-visible' },
  zoomIn: { hidden: 'scroll-zoom-hidden', visible: 'scroll-zoom-visible' },
  slideLeft: { hidden: 'scroll-left-hidden', visible: 'scroll-left-visible' },
  slideRight: { hidden: 'scroll-right-hidden', visible: 'scroll-right-visible' },
  scaleUp: { hidden: 'scroll-zoom-hidden', visible: 'scroll-zoom-visible' },
};

export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: ScrollRevealOptions = {}
) {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -50px 0px',
    delay = 0,
    animation = 'fadeUp',
  } = options;

  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let timeoutId: number | undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            timeoutId = window.setTimeout(() => setIsVisible(true), delay);
          } else {
            setIsVisible(true);
          }
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [threshold, rootMargin, delay]);

  const classes = animationClassMap[animation];
  const className = isVisible
    ? `${classes.hidden} ${classes.visible}`
    : classes.hidden;

  return { ref, isVisible, className };
}

export function useParallaxZoom<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleScroll = () => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementCenter = rect.top + rect.height / 2;
      const viewportCenter = windowHeight / 2;
      const distance = Math.abs(elementCenter - viewportCenter);
      const maxDistance = windowHeight;
      const progress = Math.max(0, 1 - distance / maxDistance);
      const scale = 0.85 + progress * 0.15;
      element.style.transform = `scale(${scale})`;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { ref };
}
